import { DarkModeHelper } from "app/helpers/darkModeHelper";
import { FreshworksHelper } from "app/helpers/freshworksHelper";
import isPublicView from "app/utils/publicViewUtils";
import isEmpty from "lodash/isEmpty";
import React, { lazy, Suspense, useEffect, useState } from "react";
import type { IntlShape } from "react-intl";
import { defineMessages, FormattedMessage, injectIntl } from "react-intl";
import { connect } from "react-redux";
import * as authAPI from "../../api/authenticationAPI";
import * as userAPI from "../../api/userApi";
import { SessionHelper } from "../../helpers/sessionHelper";
import "./AuthScreenDesktop.scss";
import "./AuthScreenMobile.scss";
import AuthScreenForgottenEmailModal from "./components/AuthScreenForgottenEmailModal/AuthScreenForgottenEmailModal";
import AuthScreenForgottenUsernameModal from "./components/AuthScreenForgottenUsernameModal/AuthScreenForgottenUsernameModal";
import AuthScreenForm from "./components/AuthScreenForm/AuthScreenForm";
import AuthScreenModal from "./components/AuthScreenModal/AuthScreenModal";
import AuthScreenResendModal from "./components/AuthScreenResendModal/AuthScreenResendModal";
import AuthScreenSubHeader from "./components/AuthScreenSubHeader/AuthScreenSubHeader";
import TwoFactorAuthModal from "./components/TwoFactorAuthModal/TwoFactorAuthModal";
//
const Footer: React.LazyExoticComponent<any> = lazy(
  () => import("../PublicScreen/components/Footer/Footer")
);

type Props = {
  history: any;
  purgeBetSlipList: (...args: Array<any>) => any;
  loginUser: (...args: Array<any>) => any;
  showNotificationDisplay: number;
  setNotificationPlatform: (...args: Array<any>) => any;
  loadNotifications: (...args: Array<any>) => any;
  intl: IntlShape;
  match: Record<string, any>;
  resetCompetitionListFilters: () => void;
};

const Auth: React.FC<Props> = ({
  history,
  purgeBetSlipList,
  loginUser,
  showNotificationDisplay,
  setNotificationPlatform,
  loadNotifications,
  intl,
  match,
  resetCompetitionListFilters,
}) => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [totpCode, setTotpCode] = useState("");
  const [resetPasswordActive, setResetPasswordActive] = useState(false);
  const [resendVerificationEmailActive, setResendVerificationEmailActive] =
    useState(false);
  const [loginError, setLoginError] = useState("");
  const [passwordResetResponse, setPasswordResetResponse] = useState<any>(null);
  const [loginLoading, setLoginLoading] = useState(false);
  const [passwordResetLoading, setPasswordResetLoading] = useState(false);
  const [modalCancelText, setModalCancelText] = useState(
    intl.formatMessage({ id: "registration-cancel" })
  );
  const [passwordResetError, setPasswordResetError] = useState("");
  const [resetUsername, setResetUsername] = useState("");
  const [resetNewPassword, setResetNewPassword] = useState("");
  const [resetPasswordConfirmation, setResetPasswordConfirmation] =
    useState("");
  const [reset, setReset] = useState(false);
  const [logUserIn, setLogUserIn] = useState(false);
  const [verifyUsername, setVerifyUsername] = useState("");
  const [resendVerificationEmailError, setResendVerificationEmailError] =
    useState("");
  const [resendVerificationEmailSuccess, setResendVerificationEmailSuccess] =
    useState("");
  const [
    submittingResetVerificationEmail,
    setSubmittingResetVerificationEmail,
  ] = useState(false);
  const [forgottenUsernameActive, setForgottenUsernameActive] = useState(false);
  const [forgottenEmailActive, setForgottenEmailActive] = useState(false);
  const [forgottenUsernameError, setForgottenUsernameError] = useState("");
  const [forgottenUsernameSuccess, setForgottenUsernameSuccess] = useState("");
  const [email, setEmail] = useState("");
  const [submittingForgottenUsername, setSubmittingForgottenUsername] =
    useState(false);
  const [twoFactorAuthModalVisible, setTwoFactorAuthModalVisible] =
    useState(false);

  useEffect(() => {
    if (!SessionHelper.isAuthenticated()) {
      purgeBetSlipList();
      resetCompetitionListFilters();
      // check for if reset password link
      if (match.url.includes("reset")) {
        setResetPasswordActive(true);
        setReset(true);
      }
    }
  }, []);
  useEffect(() => {
    if (totpCode && !twoFactorAuthModalVisible) {
      handleLogin();
    }
  }, [totpCode]);

  const closeModal = () => {
    setResetPasswordActive(false);
    setPasswordResetResponse(null);
    setPasswordResetError("");

    if (logUserIn) {
      loginUser(username, resetNewPassword);
    } else if (match.url.includes("reset") && !logUserIn) {
      setReset(false);
      history.push("/login/");
    }
  };

  const handleChange = (id: string, value: string) => {
    switch (id) {
      case "username":
        setUsername(value);
        break;
      case "password":
        setPassword(value);
        break;
      case "resetNewPassword":
        setResetNewPassword(value);
        break;
      case "resetPasswordConfirmation":
        setResetPasswordConfirmation(value);
        break;
      case "verifyUsername":
        setVerifyUsername(value);
        break;
      default:
        break;
    }
  };
  // new login flow with 2fa:
  // user logs in with username and password
  // if 2fa is not enabled, login continues same as before
  // if 2fa is enabled, api will return a flag,
  // Show a modal to allow user to enter code
  // login again with username, password and code
  // Login continues as before
  const handleLogin = (e?: Event) => {
    e?.preventDefault();
    setLoginLoading(true);

    let userHTML: any = document.getElementById("username");
    let passwordHTML: any = document.getElementById("password");

    const usernameParam = username || userHTML.value;
    const passwordParam = password || passwordHTML.value;

    loginUser(usernameParam, passwordParam, totpCode)
      .then(loginResponse => {
        if (loginResponse.requires2fa) {
          setTwoFactorAuthModalVisible(true);
          setLoginLoading(false);
          return;
        }

        if (loginResponse.success) {
          setNotificationPlatform("WEBSITE");
          loadNotifications();

          !isPublicView() && FreshworksHelper.hideWidget();
          DarkModeHelper.checkDarkModeStatus();
        }
      })
      .catch(error => {
        if (error.messages) {
          setLoginError(error.messages[0]);
          setLoginLoading(false);
          setTotpCode("");
        } else {
          setLoginLoading(false);
          setLoginError(
            "Unable to login. Username or password is not correct."
          );
        }
      });
  };

  const submitNewPassword = (e: Event) => {
    e.preventDefault();
    const newPassword = resetNewPassword;
    const passwordConfirmation = resetPasswordConfirmation;
    const resetToken = match?.params?.token;

    userAPI
      .submitNewPassword(
        username,
        newPassword,
        passwordConfirmation,
        resetToken
      )
      .then(() => {
        setModalCancelText(intl.formatMessage({ id: "button-OK" }));
        setPasswordResetResponse(
          <FormattedMessage tagName="span" id="home-passwordupdated" />
        );
        setPasswordResetLoading(true);
        setLogUserIn(true);
      })
      .catch(error => {
        if (!isEmpty(error.response.data)) {
          setPasswordResetError(
            error.response.data.errors[0].messages.toString()
          );
          setPasswordResetLoading(false);
        } else if (error.message) {
          setPasswordResetResponse(
            <FormattedMessage tagName="span" id="login-responsenegative" />
          );
          setPasswordResetLoading(false);
        }
      });
  };

  const resetPassword = (e: Event) => {
    e.preventDefault();
    userAPI
      .resetPassword(username)
      .then(resp => {
        setPasswordResetResponse(
          <FormattedMessage tagName="span" id="login-responsepositive" />
        );
        setPasswordResetLoading(false);
      })
      .catch(error => {
        if (error.message) {
          setPasswordResetResponse(
            <FormattedMessage tagName="span" id="login-responsenegative" />
          );
          setPasswordResetLoading(false);
        }
      });
  };

  const displayResendEmailVerification = () => {
    setResendVerificationEmailActive(!resendVerificationEmailActive);
  };

  const closeResendVerificationEmailModal = () => {
    setResendVerificationEmailActive(false);
    setResendVerificationEmailError("");
    setResendVerificationEmailSuccess("");
  };

  const submitResendVerificationEmail = async (e: any) => {
    e.preventDefault();
    if (isEmpty(verifyUsername)) return;

    setResendVerificationEmailError("");
    setResendVerificationEmailSuccess("");
    setSubmittingResetVerificationEmail(true);

    authAPI
      .submitResendVerificationEmail(verifyUsername)
      .then((resp: any) => {
        setSubmittingResetVerificationEmail(false);
        setResendVerificationEmailError("");
        setResendVerificationEmailSuccess(resp);
      })
      .catch((error: any) => {
        setResendVerificationEmailError(error.message);
        setSubmittingResetVerificationEmail(false);
      });
  };

  const displayForgottenUsername = () => {
    setForgottenUsernameActive(!forgottenUsernameActive);
  };

  const closeForgottenUsernameModal = () => {
    setForgottenUsernameActive(false);
  };

  const toggleSubmittingForgottenUsernameLoader = () => {
    setSubmittingForgottenUsername(!submittingForgottenUsername);
  };

  const displayForgottenEmail = () => {
    setForgottenEmailActive(!forgottenEmailActive);
  };

  const closeForgottenEmailModal = () => {
    setForgottenEmailActive(false);
  };

  const I18n = defineMessages({
    username: {
      id: "login-username",
    },
    password: {
      id: "login-password",
    },
    newpassword: {
      id: "profile-new-password",
    },
    retypepassword: {
      id: "profile-retype-password",
    },
    email: {
      id: "home-contact-us-input-email",
    },
    firstname: {
      id: "registrations-first-name",
    },
    lastname: {
      id: "registrations-last-name",
    },
    newPasswordPlaceholder: {
      id: "profile-new-password-placeholder",
    },
    retypePasswordPlaceholder: {
      id: "profile-retype-password-placeholder",
    },
  });

  return (
    <div
      className="auth-screen"
      style={{
        paddingTop: showNotificationDisplay ? "98px" : "56px",
      }}
    >
      <AuthScreenSubHeader />
      <AuthScreenForm
        login={handleLogin}
        loginLoading={loginLoading}
        handleChange={handleChange}
        I18n={I18n}
        forgottenPassword={() => {
          setResetPasswordActive(true);
        }}
        loginError={loginError}
        displayResendEmailVerification={displayResendEmailVerification}
        displayForgottenUsername={displayForgottenUsername}
        displayForgottenEmail={displayForgottenEmail}
      />
      <Suspense fallback={<div className="fallback">loading...</div>}>
        <Footer />
      </Suspense>
      <Suspense fallback={<div className="fallback">loading...</div>}>
        <AuthScreenModal
          resetPasswordActive={resetPasswordActive}
          I18n={I18n}
          closeModal={closeModal}
          modalCancelText={modalCancelText}
          passwordResetResponse={passwordResetResponse}
          handleChange={handleChange}
          passwordResetLoading={passwordResetLoading}
          passwordResetError={passwordResetError}
          submitNewPassword={submitNewPassword}
          reset={reset}
          resetPassword={resetPassword}
          resetNewPassword={resetNewPassword}
          confirmPassword={resetPasswordConfirmation}
        />
        <AuthScreenResendModal
          resendVerificationEmailActive={resendVerificationEmailActive}
          I18n={I18n}
          handleChange={handleChange}
          closeModal={closeResendVerificationEmailModal}
          submitResendVerificationEmail={submitResendVerificationEmail}
          resendVerificationEmailError={resendVerificationEmailError}
          resendVerificationEmailSuccess={resendVerificationEmailSuccess}
          submittingResetVerificationEmail={submittingResetVerificationEmail}
        />
        <AuthScreenForgottenUsernameModal
          forgottenUsernameActive={forgottenUsernameActive}
          I18n={I18n}
          handleChange={handleChange}
          closeModal={closeForgottenUsernameModal}
          submitForgottenUsername={toggleSubmittingForgottenUsernameLoader}
          submittingForgottenUsername={submittingForgottenUsername}
        />
        <AuthScreenForgottenEmailModal
          forgottenEmailActive={forgottenEmailActive}
          I18n={I18n}
          closeModal={closeForgottenEmailModal}
        />
        <TwoFactorAuthModal
          isOpen={twoFactorAuthModalVisible}
          onClose={() => setTwoFactorAuthModalVisible(false)}
          onVerify2fa={code => {
            setTotpCode(code);
            setTwoFactorAuthModalVisible(false);
          }}
        />
      </Suspense>
    </div>
  );
};

const mapStateToProps = state => ({
  showNotificationDisplay: state.notificationListModel.showNotificationDisplay,
});

const mapDispatchToProps = dispatch => ({
  loadNotifications: () => {
    dispatch.notificationListModel.loadNotificationList();
  },
  setNotificationPlatform: platform => {
    dispatch.notificationListModel.setPlatform(platform);
  },
  purgeBetSlipList: () => {
    dispatch.betSlipListModel.purgeBetSlipList();
  },
  resetCompetitionListFilters: () => {
    dispatch.competitionsListFiltersModel.resetCompetitionListFilters();
    dispatch.asianCompetitionListFiltersModel.resetCompetitionListFilters();
  },
  loginUser: (username, password, totpCode) =>
    dispatch.sessionModel.loginUser({
      username,
      password,
      totpCode,
    }),
});

export default injectIntl(
  connect(mapStateToProps, mapDispatchToProps)(Auth)
) as any;
